import { AnimateStaggerElement } from '@dreamstack/feature-components/src/layout/AnimateStagger'
import { GridWithGaps } from '@dreamstack/feature-components/src/layout/MainGrid'
import { staggerWrapperVariants } from '@dreamstack/feature-components/src/layout/AnimateStagger'
import { assertPopulatedDeep } from '@dreamstack/payload/src/lib/assertPopulated'
import { BlockPartnerGrid } from '@dreamstack/payload/src/payload-types'
import { useAnimationOnScreen } from '@dreamstack/util'
import { cn } from '@dreamstack/util/src/cn'
import { motion } from 'framer-motion'
import map from 'lodash/map'
import Image from 'next/image'
import { forwardRef, type FunctionComponent } from 'react'

const StyledPartnerGrid = forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof motion.div>
>(({ className, ...rest }, ref) => (
  <motion.div
    ref={ref}
    className={cn(
      'col-span-full grid grid-cols-2 gap-x-px-24 gap-y-px-48',
      'md:grid-cols-4 md:gap-x-0',
      className
    )}
    {...rest}
  />
))

StyledPartnerGrid.displayName = 'StyledPartnerGrid'

const StyledPartnerText: React.FC<
  React.HTMLAttributes<HTMLParagraphElement>
> = ({ className, ...rest }) => (
  <p
    className={cn(
      'text-px-13 leading-px-18 mt-px-32 text-center',
      'lg:text-px-16 lg:leading-px-24 lg:mt-px-40',
      className
    )}
    {...rest}
  />
)

const StyledPartnerEntry: React.FC<
  React.HTMLAttributes<HTMLDivElement> & { isLast: boolean }
> = ({ isLast, className, ...rest }) => (
  <div
    className={cn(
      'md:p-px-24',
      !isLast && 'md:border-r-2 md:border-accentroGray-200',
      className
    )}
    {...rest}
  />
)

const StyledLink: React.FC<
  React.PropsWithChildren<React.AnchorHTMLAttributes<HTMLAnchorElement>> & {
    hasHref?: boolean
  }
> = ({ hasHref, className, children, ...rest }) => (
  <a className={cn(hasHref && 'cursor-pointer', className)} {...rest}>
    {children}
  </a>
)

const PartnerContainer: FunctionComponent<
  React.PropsWithChildren<{ href?: string }>
> = ({ href, children }) => {
  const hasHref = !!href
  if (hasHref)
    return (
      <StyledLink hasHref={hasHref} target={'_blank'} href={href}>
        {children}
      </StyledLink>
    )
  else return <>{children}</>
}

export const PartnerGrid: FunctionComponent<
  React.PropsWithChildren<{
    block: BlockPartnerGrid
  }>
> = ({ block }) => {
  const { controls, ref } = useAnimationOnScreen()

  return (
    <>
      <GridWithGaps>
        <StyledPartnerGrid
          ref={ref}
          initial="hidden"
          animate={controls}
          variants={staggerWrapperVariants}
        >
          {map(block.partners, (partner, index) => {
            assertPopulatedDeep(partner)
            const isLastInRow = (index + 1) % 4 === 0
            const partnerImage = partner?.partnerImage
            return (
              <AnimateStaggerElement key={index}>
                <PartnerContainer href={partner?.href ?? '#'}>
                  <StyledPartnerEntry isLast={isLastInRow}>
                    {!!partnerImage?.url && (
                      <div className="aspect-[2/1] relative">
                        <Image
                          className="object-cover"
                          fill
                          src={partnerImage?.url}
                          alt={partnerImage?.alt || 'partner image'}
                        />
                      </div>
                    )}
                    <StyledPartnerText>
                      {partner?.partnerText}
                    </StyledPartnerText>
                  </StyledPartnerEntry>
                </PartnerContainer>
              </AnimateStaggerElement>
            )
          })}
        </StyledPartnerGrid>
      </GridWithGaps>
    </>
  )
}
